import 'vite/modulepreload-polyfill';

import * as DataDogLogs from '~/lib/datadog/datadog';

const API_KEYWORDS = {
  DEVICE_TYPE_DESKTOP: 'desktop',
  DEVICE_TYPE_MOBILE: 'mobile',
  PDP_SIGNUP_FLOW_SOLD_OUT: 'soldOut',
};

let API_KEY = '';

/*
 * Initialize Iterable SDK when DOM is ready
 *
 */
document.addEventListener('DOMContentLoaded', () => {
  const { env, types } = window.ALLBIRDS.environment;
  const iterableKeys = window.ALLBIRDS.Iterable || {};
  const ITERABLE_API_KEY_PROD = iterableKeys.prodKey || '';
  const ITERABLE_API_KEY_STAGING = iterableKeys.stagingKey || '';
  API_KEY =
    env === types.STAGING || env === types.DEVELOPMENT
      ? ITERABLE_API_KEY_STAGING
      : ITERABLE_API_KEY_PROD;
});

/**
 * Handles pushing signup data to Iterable
 *
 * @param {Object} payload - The payload containing email and signup source
 * @property {string} email - The email address of the user
 * @property {string} signupSource - The source of the signup
 *
 * @returns {void}
 */
const pushSignUp = (payload) => {
  const { email, signupSource } = payload;
  if (!API_KEY) {
    DataDogLogs.error(
      '[Allbirds pixel] - Iterable - Error: No API KEY configured',
      {},
      new Error('No API KEY configured'),
    );

    return;
  }

  if (!email) {
    DataDogLogs.error(
      '[Allbirds pixel] - Iterable - Error: Email is required',
      {},
      new Error('Email is required'),
    );

    return;
  }

  if (!signupSource) {
    DataDogLogs.error(
      '[Allbirds pixel] - Iterable - Error: Signup source is required',
      {},
      new Error('Signup source is required'),
    );

    return;
  }

  try {
    window._iaq = window._iaq || [];
    window._iaq.push(['account', API_KEY]);
    window._iaq.push(['identify', email, payload]);
    window._iaq.push(['track', signupSource, { signupChannel: 'web' }]);
  } catch (error) {
    DataDogLogs.error(
      '[Allbirds pixel] - Iterable - Error: Failed to push signup data',
      {
        signupSource,
      },
      error,
    );
  }
};

/**
 * Builds the product data object for the Iterable payload
 *
 * @param {Object} payload - The payload containing product data
 * @property {string} productColor - The color of the product
 * @property {string} productEdition - The edition of the product
 * @property {string} productGender - The gender of the product
 * @property {string} productMaster - The master of the product
 * @property {string} productMaterial - The material of the product
 * @property {string} productName - The name of the product
 * @property {string} productSilhouette - The silhouette of the product
 * @property {string} sku - The SKU of the product
 *
 * @returns {Object} - The product data object
 */
const buildProductDataObject = ({
  productColor = '',
  productEdition = '',
  productGender = '',
  productMaster = '',
  productMaterial = '',
  productName = '',
  productSilhouette = '',
  sku = '',
}) => {
  return {
    categories: {
      allbirdsEdition: productEdition,
      allbirdsGender: productGender,
      allbirdsHue: productColor,
      allbirdsMaster: productMaster,
      allbirdsMaterial: productMaterial,
      allbirdsSilhouette: productSilhouette,
    },
    name: productName,
    sku,
  };
};

/**
 * Builds the Iterable payload for pushing data
 *
 * @param {Object} payload - The payload containing email and signup source
 * @property {string} email - The email address of the user
 * @property {string} isMobile - The device type of the user
 * @property {boolean} pdpSignupFlow - Whether the signup flow is on a PDP
 * @property {string} placementSource - The source of the signup
 * @property {Object} productData - Product data to be included in the payload, check buildProductDataObject() for more details
 *
 * @returns {Object} - The Iterable payload
 */
const buildPayload = ({ email, isMobile, pdpSignupFlow, placementSource, productData = null }) => {
  let result = {};

  try {
    const { shopId } = window.ALLBIRDS.environment;
    const currentLocale = window.ALLBIRDS.environment.defaultLocale;
    const signupSourceURL = window.location.pathname;

    // Base payload
    result = {
      accepts_marketing: true,
      deviceType: isMobile.value
        ? API_KEYWORDS.DEVICE_TYPE_MOBILE
        : API_KEYWORDS.DEVICE_TYPE_DESKTOP,
      email,
      locale: currentLocale,
      pdpSignupFlow,
      signupSource: placementSource,
      signupSourceURL,
      siteRegion: shopId,
    };

    // Add product data if it exists
    if (productData && Object.keys(productData).length > 0) {
      result = {
        ...result,
        productData,
      };
    }
  } catch (error) {
    DataDogLogs.error(
      '[Allbirds pixel] - Iterable - Error: Failed to build payload',
      {
        isMobile,
        placementSource,
      },
      error,
    );
  }

  return result;
};

export { API_KEYWORDS, buildPayload, buildProductDataObject, pushSignUp };
